import React from 'react';
import { Card, IconButton, Link } from '@mui/material';

import { MDBox, MDTypography, MDButton, FaIcon } from '@components';
import brandLogo from '@assets/images/brand.jpg';
import { sendAmplitudeData } from '@utils/amplitude';
import { CLICKED_EXPLAINER_VIDEO } from '@constants/eventNames';

interface EmptyProjectsProps {
  setIsUploadModalOpen: () => void;
}

const EmptyProjects: React.FC<EmptyProjectsProps> = ({
  setIsUploadModalOpen
}) => {
  const videoUrl = `https://www.youtube.com/watch?v=rUVG93r24a8`;

  return (
    <Card
      sx={() => ({
        display: 'flex',
        width: '100%',
        mt: 2,
        py: '10px',
        pl: '20px',
        height: '85vh',
        flexDirection: 'column'
      })}
    >
      <MDBox
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'flex',
          width: '100%',
          height: '42.5vh',
          flexDirection: { md: 'row' }
        }}
      >
        <MDBox
          sx={{
            textAlign: 'center',
            flex: '0 0 20%'
          }}
        >
          <MDTypography
            variant='xl'
            color='info'
            textGradient
            sx={{ lineHeight: 'initial' }}
          >
            Create a project and add your data here
          </MDTypography>
          <MDTypography
            variant='sm'
            color='secondary'
            sx={{ display: 'block' }}
          >
            (.xlsx, .csv , google sheet or via API)
          </MDTypography>
          <MDBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 3
            }}
          >
            <MDButton
              color='light'
              size='medium'
              circular={false}
              onClick={setIsUploadModalOpen}
            >
              <FaIcon
                typographyProps={{ variant: 'xs', color: 'primary' }}
                icon='plus-square'
              />
              <MDTypography pl={1} variant='xs' color='info'>
                NEW PROJECT
              </MDTypography>
            </MDButton>
          </MDBox>

          <a
            target='_blank'
            rel='noreferrer'
            href='https://www.notion.so/datasquirrel/Sample-Files-e015740bb5de4e0b81727b8fc7e99ca9?pvs=4'
            style={{
              lineHeight: 1,
              textAlign: 'center',
              borderBottom: '1px solid #236DA9',
              verticalAlign: 'top'
            }}
          >
            <MDTypography variant='sm' color='secondary'>
              Or select a Sample file from here
            </MDTypography>
          </a>
        </MDBox>
        <FaIcon
          icon='long-arrow-alt-right'
          typographyProps={{
            color: 'grey',
            sx: {
              margin: { xs: '16px', md: '16px' },
              transform: { xs: 'rotate(90deg)', md: 'rotate(0)' }
            }
          }}
        />
        <MDBox
          sx={{
            textAlign: 'center',
            flex: '0 0 20%'
          }}
        >
          <MDTypography variant='xl' color='grey' mb={3}>
            Review Uploaded data
          </MDTypography>
          <MDTypography variant='sm' color='grey' sx={{ display: 'block' }}>
            (e.g. which column data to import)
          </MDTypography>
        </MDBox>
        <FaIcon
          icon='long-arrow-alt-right'
          typographyProps={{
            color: 'grey',
            sx: {
              margin: { xs: '16px', md: '16px' },
              transform: { xs: 'rotate(90deg)', md: 'rotate(0)' }
            }
          }}
        />
        <MDBox
          sx={{
            textAlign: 'center',
            flex: '0 0 20%'
          }}
        >
          <MDTypography
            variant='xl'
            color='grey'
            mb={3}
            sx={{ lineHeight: 'initial' }}
          >
            Clean & Analyse data
          </MDTypography>
          <MDTypography
            variant='sm'
            color='grey'
            sx={{ display: 'block', lineHeight: 'initial' }}
          >
            (Use AI or Create charts)
          </MDTypography>
        </MDBox>
        <FaIcon
          icon='long-arrow-alt-right'
          typographyProps={{
            color: 'grey',
            sx: {
              margin: { xs: '16px', md: '16px' },
              transform: { xs: 'rotate(90deg)', md: 'rotate(0)' }
            }
          }}
        />
        <MDBox
          sx={{
            textAlign: 'center',
            flex: '0 0 15%'
          }}
        >
          <MDTypography
            variant='xl'
            color='grey'
            mb={3}
            sx={{ lineHeight: 'initial' }}
          >
            Create Reports
          </MDTypography>
          <MDTypography
            variant='sm'
            color='grey'
            sx={{ display: 'block', lineHeight: 'initial' }}
          >
            (Download and Share)
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox
        display='flex'
        height='42.5vh'
        flexDirection='row'
        width='100%'
        mt={5}
        justifyContent='space-between'
        alignItems='center'
      >
        <MDBox display='flex' flexDirection='row' width='100%'>
          <MDBox display='flex' flexDirection='column' gap={3} width='65%'>
            <MDBox
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={2}
            >
              <FaIcon
                typographyProps={{ variant: 'lgg', color: 'success' }}
                icon='table'
              />
              <MDTypography variant='sm' color='secondary'>
                DataSquirrel has a robust algorithm designed to read your data
                efficiently. However, its automated cleaning, analysis, and
                insights are most effective with tabular data.{' '}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://datasquirrel.notion.site/Want-to-know-more-about-Importing-data-into-DataSquirrel-ea284f2ef9f142939a2802ada6ff11b0'
                  style={{
                    lineHeight: 1,
                    textAlign: 'center',
                    borderBottom: '1px solid #236DA9',
                    verticalAlign: 'top'
                  }}
                >
                  <MDTypography variant='sm' color='secondary'>
                    Learn more here.
                  </MDTypography>
                </a>{' '}
              </MDTypography>
            </MDBox>
            <MDBox
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={2}
            >
              <FaIcon
                typographyProps={{ variant: 'lgg', color: 'success' }}
                icon='shield-check'
              />
              <MDTypography variant='sm' color='secondary'>
                DataSquirrel is GDPR/PDPA compliant, undergoes regular SAST/
                DAST/ Pen tests, and never shares raw data with language models
                like ChatGPT.{' '}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href=' https://datasquirrel.notion.site/Terms-Conditions-1b41ca5daffe4d418770998ddf0e4fa7'
                  style={{
                    lineHeight: 1,
                    textAlign: 'center',
                    borderBottom: '1px solid #236DA9',
                    verticalAlign: 'top'
                  }}
                >
                  <MDTypography variant='sm' color='secondary'>
                    Learn more here.
                  </MDTypography>
                </a>{' '}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox
            component={Link}
            href={videoUrl}
            target='_blank'
            sx={{
              display: 'flex',
              height: '13rem',
              width: '35%',
              backgroundImage: `url(${brandLogo})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              borderRadius: '10px',
              mx: 5
            }}
            onClick={() => sendAmplitudeData(CLICKED_EXPLAINER_VIDEO)}
          >
            <IconButton
              sx={{
                zIndex: 1000
              }}
            >
              <FaIcon
                typographyProps={{
                  variant: 'lgg',
                  color: 'white',
                  fontSize: '3rem'
                }}
                icon='play'
              />
            </IconButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default EmptyProjects;
